import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Locker } from '@sorare/core/src/atoms/icons/Locker';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { formatScarcity } from '@sorare/core/src/lib/cards';
import { validateVaultRequirements } from '@sorare/core/src/lib/so5';

import { RarityVaultBannerWithFetch } from '@sorare/marketplace/src/components/sealing/RarityVaultBanner/index.withFetch';
import { RarityVaultHowItWorks } from '@sorare/marketplace/src/components/sealing/RarityVaultHowItWorks';
import { VAULT_PROGRESS_BAR_COLORS } from '@sorare/marketplace/src/components/sealing/constant';

import type {
  LeagueTrckVaultGatedHowItWorksDialog_sealingConfig,
  LeagueTrckVaultGatedHowItWorksDialog_so5Leaderboard,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ center: true, gap: 4 })`
  padding: var(--triple-unit);

  & > * {
    max-width: calc(46 * var(--unit)); /* 368px */
  }
`;

type Props = {
  open: boolean;
  so5Leaderboard?: LeagueTrckVaultGatedHowItWorksDialog_so5Leaderboard;
  sealingConfig?: LeagueTrckVaultGatedHowItWorksDialog_sealingConfig;
  loading: boolean;
  onClose: () => void;
  rarity: Rarity;
};

export const LeagueTrckVaultGatedHowItWorksDialog = ({
  open = true,
  sealingConfig,
  so5Leaderboard,
  rarity,
  loading,
  onClose,
}: Props) => {
  const { minVaultBoost } = validateVaultRequirements(so5Leaderboard);

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={onClose}
      title={
        <Vertical center gap={0}>
          <LabelL bold>{so5Leaderboard?.shortDisplayName}</LabelL>
          <LabelM
            className="text-gradient-color"
            style={
              {
                '--text-gradient-color': VAULT_PROGRESS_BAR_COLORS[rarity],
              } as CSSProperties
            }
          >
            {formatScarcity(rarity)}
          </LabelM>
        </Vertical>
      }
    >
      {!loading && (
        <Root>
          <Vertical gap={3} center>
            <Locker width={64} height={64} />
            <LabelM as={Horizontal} gap={1} color="var(--c-yellow-400)">
              <FontAwesomeIcon
                icon={faExclamationCircle}
                color="var(--c-yellow-400)"
              />
              <FormattedMessage
                id="Competitions.rules.vaultRequirements2"
                defaultMessage="Requires {rarity} Vault Lvl {minVaultLevel}"
                values={{
                  minVaultLevel: minVaultBoost?.boostLevel.milestone,
                  rarity: formatScarcity(rarity),
                }}
              />
            </LabelM>
            <RarityVaultBannerWithFetch
              rarity={rarity}
              minimumPointsRequired={minVaultBoost?.points}
              isBoostLevelRequirementBanner
              showHowItWorksCta="onlyIcon"
            />
          </Vertical>

          <RarityVaultHowItWorks sealingConfig={sealingConfig} />
        </Root>
      )}
    </Dialog>
  );
};

LeagueTrckVaultGatedHowItWorksDialog.fragments = {
  sealingConfig: gql`
    fragment LeagueTrckVaultGatedHowItWorksDialog_sealingConfig on SealingConfig {
      ...RarityVaultHowItWorks_sealingConfig
    }
    ${RarityVaultHowItWorks.fragments.sealingConfig}
  ` as TypedDocumentNode<LeagueTrckVaultGatedHowItWorksDialog_sealingConfig>,
  so5Leaderboard: gql`
    fragment LeagueTrckVaultGatedHowItWorksDialog_so5Leaderboard on So5Leaderboard {
      slug
      shortDisplayName: displayName(short: true)
      ...validateVaultRequirements_so5Leaderboard
    }
    ${validateVaultRequirements.fragments.so5Leaderboard}
  ` as TypedDocumentNode<LeagueTrckVaultGatedHowItWorksDialog_so5Leaderboard>,
};
